import { devLog, devError } from '../utils/devLogger';
/**
 * Admin Document Upload Service
 *
 * Allows admins and facilitators to upload documents on behalf of learners
 * with comprehensive audit trails for POPIA compliance.
 *
 * Features:
 * - Search learners by ID or email
 * - Role-based access control
 * - Document upload with audit trail
 * - Bulk upload capabilities
 * - File validation and security
 */

import { auth, db, storage } from '../firebase';
import {
  collection,
  getDocs,
  addDoc,
  setDoc,
  query,
  where,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc,
  Timestamp
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_CATEGORIES_BY_ID,
  DOCUMENT_CATEGORIES_BY_NAME,
  DOCUMENT_VALIDATION
} from '../config/documentCategories';
import { toCategoryId } from '../constants/documentCategories';
import { validateFile } from '../utils/inputValidation';
import auditTrailService from './auditTrailService';

const resolveCategoryConfig = (input) => {
  if (!input) {
    return null;
  }

  const raw = String(input).trim();
  const upper = raw.toUpperCase();
  const slug = toCategoryId(raw);
  const normalized = raw.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

  const aliasMap = {
    'certified id': 'Certified ID Copy - not older than 3 months',
    'certified id copy': 'Certified ID Copy - not older than 3 months',
    'id copy': 'Certified ID Copy - not older than 3 months',
    'certified matric': 'Certified Matric Certificate - not older than 3 months',
    'matric certificate': 'Certified Matric Certificate - not older than 3 months',
    'highest qualification': 'Certified Highest Qualification - not older than 3 months'
  };

  const aliasName = aliasMap[normalized];
  if (aliasName && DOCUMENT_CATEGORIES_BY_NAME[aliasName]) {
    return DOCUMENT_CATEGORIES_BY_NAME[aliasName];
  }

  const directMatch = (
    DOCUMENT_CATEGORIES[upper] ||
    DOCUMENT_CATEGORIES_BY_ID[raw] ||
    DOCUMENT_CATEGORIES_BY_ID[slug] ||
    DOCUMENT_CATEGORIES_BY_NAME[raw] ||
    DOCUMENT_CATEGORIES_BY_NAME[raw.replace(/_/g, ' ')] ||
    null
  );
  if (directMatch) {
    return directMatch;
  }

  // Fuzzy fallback for UI label variations (e.g. "Certified ID")
  const byName = Object.values(DOCUMENT_CATEGORIES_BY_NAME).find((category) => {
    const categoryNameNormalized = String(category?.name || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
    return categoryNameNormalized === normalized;
  });
  if (byName) return byName;

  if (normalized.includes('certified id')) {
    return DOCUMENT_CATEGORIES_BY_NAME['Certified ID Copy - not older than 3 months'] || null;
  }

  if (normalized.includes('matric')) {
    return DOCUMENT_CATEGORIES_BY_NAME['Certified Matric Certificate - not older than 3 months'] || null;
  }

  return null;
};

class AdminDocumentUploadService {
  constructor() {
    this.currentUser = null;
    this.userRole = null;
  }

  /**
   * Initialize service with current user context
   */
  async initialize() {
    this.currentUser = auth.currentUser;
    if (this.currentUser) {
      const userDoc = await getDoc(doc(db, 'users', this.currentUser.uid));
      this.userRole = userDoc.data()?.role;
    }
  }

  /**
   * Check if current user has permission to upload documents for others
   */
  hasUploadPermission() {
    return this.userRole === 'admin' || this.userRole === 'facilitator' || this.userRole === 'qa';
  }

  /**
   * Check if facilitator has access to specific learner (based on cohort)
   */
  async canAccessLearner(learnerId) {
    try {
      // Admin role: access to all learners
      if (this.userRole === 'admin' || this.userRole === 'qa') {
        return true;
      }

      // Facilitator role: check program/cohort assignments
      if (this.userRole === 'facilitator') {
        const facilitatorDoc = await getDoc(doc(db, 'users', this.currentUser.uid));

        if (!facilitatorDoc.exists()) {
          devError('Facilitator document not found:', this.currentUser.uid);
          return false;
        }

        const facilitatorData = facilitatorDoc.data();
        const assignedPrograms = facilitatorData?.assignedPrograms || [];
        const assignedCohorts = facilitatorData?.assignedCohorts || [];

        // If facilitator has no assignments, deny access
        if (assignedPrograms.length === 0 && assignedCohorts.length === 0) {
          devLog('Facilitator has no program or cohort assignments');
          return false;
        }

        // Get learner data
        const learnerDoc = await getDoc(doc(db, 'users', learnerId));

        if (!learnerDoc.exists()) {
          devError('Learner document not found:', learnerId);
          return false;
        }

        const learnerData = learnerDoc.data();

        // Check program-based access
        if (assignedPrograms.length > 0 && learnerData.programId) {
          if (assignedPrograms.includes(learnerData.programId)) {
            devLog('✅ Facilitator has program access to learner:', learnerData.displayName);
            return true;
          }
        }

        // Check cohort-based access
        if (assignedCohorts.length > 0 && learnerData.cohortId) {
          if (assignedCohorts.includes(learnerData.cohortId)) {
            devLog('✅ Facilitator has cohort access to learner:', learnerData.displayName);
            return true;
          }
        }

        devLog('❌ Facilitator does not have access to learner:', learnerData.displayName);
        return false;
      }

      // Other roles: no access to learners by default
      return false;

    } catch (error) {
      devError('Error checking learner access:', error);
      return false;
    }
  }

  /**
   * Search for learners by ID number, email, or name
   */
  async searchLearners(searchTerm, limit = 20) {
    if (!this.hasUploadPermission()) {
      throw new Error('Access denied: Insufficient permissions');
    }

    const searchTermTrimmed = searchTerm.trim();

    if (searchTermTrimmed.length < 2) {
      return [];
    }

    try {
      devLog('🔍 Starting learner search with term:', searchTermTrimmed);

      // First, get all learners and then filter client-side for better case-insensitive search
      const allLearnersQuery = query(
        collection(db, 'users'),
        where('role', '==', 'learner')
      );

      const allLearnersSnapshot = await getDocs(allLearnersQuery);
      devLog('📊 Total learners found in database:', allLearnersSnapshot.docs.length);

      const allResults = new Map();
      const searchTermLower = searchTermTrimmed.toLowerCase();

      // Filter results client-side for case-insensitive search
      allLearnersSnapshot.docs.forEach(doc => {
        const userData = { id: doc.id, ...doc.data() };

        // Check if search term matches any field (case-insensitive)
        const email = (userData.email || '').toLowerCase();
        const displayName = (userData.displayName || '').toLowerCase();
        const idNumber = (userData.idNumber || '').toLowerCase();
        const firstName = (userData.firstName || '').toLowerCase();
        const lastName = (userData.lastName || '').toLowerCase();

        const isMatch = email.includes(searchTermLower) ||
                       displayName.includes(searchTermLower) ||
                       idNumber.includes(searchTermLower) ||
                       firstName.includes(searchTermLower) ||
                       lastName.includes(searchTermLower);

        if (isMatch) {
          // Filter by facilitator access if needed
          if (this.userRole === 'facilitator') {
            // This will be checked later when accessing specific learner
            allResults.set(doc.id, userData);
          } else {
            allResults.set(doc.id, userData);
          }
        }
      });

      devLog('📋 Filtered results count:', allResults.size);

      const results = Array.from(allResults.values())
        .slice(0, limit)
        .map(user => ({
          id: user.id,
          displayName: user.displayName,
          email: user.email,
          idNumber: user.idNumber,
          cohortId: user.cohortId,
          province: user.province,
          status: user.status || user.isActive !== false ? 'active' : 'inactive'
        }));

      devLog('✅ Final search results:', results.length);

      // Log search action (with error handling)
      try {
        await auditTrailService.logDataAccess('learner_search', searchTerm, 'search');
      } catch (auditError) {
        devLog('⚠️ Failed to log audit trail for search:', auditError);
        // Don't fail the search if audit logging fails
      }

      return results;

    } catch (error) {
      devError('❌ Error searching learners:', error);
      throw new Error('Failed to search learners: ' + error.message);
    }
  }

  /**
   * Get learner details including document status
   */
  async getLearnerDetails(learnerId) {
    if (!this.hasUploadPermission()) {
      throw new Error('Access denied: Insufficient permissions');
    }

    const canAccess = await this.canAccessLearner(learnerId);
    if (!canAccess) {
      throw new Error('Access denied: Cannot access this learner');
    }

    try {
      const learnerDoc = await getDoc(doc(db, 'users', learnerId));

      if (!learnerDoc.exists()) {
        throw new Error('Learner not found');
      }

      const learnerData = learnerDoc.data();

      // Get learner's documents
      const documentsQuery = query(
        collection(db, 'users', learnerId, 'documents')
      );
      const documentsSnapshot = await getDocs(documentsQuery);

      const documents = {};
      const uniqueDocIds = new Set(); // Track unique document IDs to avoid duplication

      documentsSnapshot.docs.forEach((docSnapshot) => {
        const docData = docSnapshot.data();
        const docId = docSnapshot.id;

        // Skip if we've already processed this document
        if (uniqueDocIds.has(docId)) {
          return;
        }
        uniqueDocIds.add(docId);

        const record = {
          id: docId,
          ...docData
        };

        // Use the primary key based on priority: categoryKey > categoryId > category name
        // This ensures we only add each document ONCE under its primary key
        // We use categoryKey (legacy key like "CERTIFIED_ID_COPY") as primary because AdminDocumentUpload expects it
        const primaryKey = docData.categoryKey || docData.categoryId || docData.category;

        if (primaryKey) {
          documents[primaryKey] = record;
        }
      });

      // Log access (with error handling)
      try {
        await auditTrailService.logDataAccess('learner_details', learnerId, 'read');
      } catch (auditError) {
        devLog('⚠️ Failed to log audit trail for learner details access:', auditError);
        // Don't fail the operation if audit logging fails
      }

      return {
        id: learnerId,
        displayName: learnerData.displayName,
        email: learnerData.email,
        idNumber: learnerData.idNumber,
        cohortId: learnerData.cohortId,
        province: learnerData.province,
        status: learnerData.status || 'active',
        documents: documents,
        registrationDate: learnerData.createdAt
      };

    } catch (error) {
      devError('Error getting learner details:', error);
      throw new Error('Failed to get learner details: ' + error.message);
    }
  }

  /**
   * Upload document on behalf of a learner
   */
  async uploadDocumentForLearner(learnerId, file, category, additionalInfo = {}) {
    await this.initialize();

    if (!this.hasUploadPermission()) {
      throw new Error('Access denied: Insufficient permissions');
    }

    const canAccess = await this.canAccessLearner(learnerId);
    if (!canAccess) {
      throw new Error('Access denied: Cannot upload for this learner');
    }

    const fileValidation = validateFile(file, {
      maxSize: DOCUMENT_VALIDATION.MAX_FILE_SIZE,
      allowedTypes: DOCUMENT_VALIDATION.ALLOWED_FORMATS.map((ext) => ext.replace('.', '')),
      allowedMimeTypes: DOCUMENT_VALIDATION.ALLOWED_MIME_TYPES
    });

    if (!fileValidation.isValid) {
      throw new Error('File validation failed: ' + fileValidation.errors.join(', '));
    }

    const categoryInfo = resolveCategoryConfig(category);
    if (!categoryInfo) {
      throw new Error('Invalid document category');
    }

    try {
      // Get learner details for user-friendly file naming
      const learnerDoc = await getDoc(doc(db, 'users', learnerId));
      const learnerData = learnerDoc.data();

      // Create user-friendly file name: NameSurname_IDNumber_DocumentType.ext
      const getUserName = () => {
        if (learnerData?.firstName && learnerData?.lastName) {
          return `${learnerData.firstName}${learnerData.lastName}`;
        } else if (learnerData?.displayName) {
          return learnerData.displayName.replace(/\s+/g, '');
        }
        return 'User';
      };

      const getIdNumber = () => {
        return learnerData?.idNumber || 'NoID';
      };

      const sanitizeName = (name) => {
        return name.replace(/[^a-zA-Z0-9]/g, '');
      };

      const getFileExtension = (filename) => {
        const parts = filename.split('.');
        return parts.length > 1 ? parts[parts.length - 1] : 'pdf';
      };

      const userName = sanitizeName(getUserName());
      const idNumber = getIdNumber();
      const documentType = sanitizeName(categoryInfo.name.replace(/\s+/g, ''));
      const fileExtension = getFileExtension(file.name);
      const customFileName = `${userName}_${idNumber}_${documentType}.${fileExtension}`;

      const documentsCollection = collection(db, 'users', learnerId, 'documents');
      const documentRef = doc(documentsCollection);
      const storagePath = `users/${learnerId}/documents/${documentRef.id}/${customFileName}`;
      const storageRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(storageRef, file);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            devLog(`Upload progress: ${progress}%`);
          },
          (error) => {
            devError('Upload error:', error);
            reject(new Error('Upload failed: ' + error.message));
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              const uploadedAt = serverTimestamp();
              const now = Timestamp.now();
              const certifiedInput = additionalInfo.certifiedAt || additionalInfo.certifiedDate || null;
              const certifiedDate = certifiedInput ? new Date(certifiedInput) : null;
              const certifiedTimestamp =
                certifiedDate && !Number.isNaN(certifiedDate.getTime())
                  ? Timestamp.fromDate(certifiedDate)
                  : null;
              const variant = categoryInfo.allowedVariants?.length
                ? additionalInfo.documentVariant || additionalInfo.variant || null
                : null;

              const documentData = {
                category: categoryInfo.name,
                categoryId: categoryInfo.id,
                categoryKey: categoryInfo.key,
                uploadedAt,
                uploadDate: uploadedAt,
                certifiedAt: certifiedTimestamp,
                status: 'verified', // Auto-verify admin and facilitator uploads
                notes: additionalInfo.notes || '',
                uploadedBy: this.currentUser.uid,
                uploadedByEmail: this.currentUser.email,
                uploadedByRole: this.userRole,
                adminUpload: true,
                adminUploadReason: additionalInfo.reason || 'Administrative upload',
                fileName: customFileName,
                fileURL: downloadURL,
                filePath: storagePath,
                files: [
                  {
                    name: customFileName,
                    type: file.type,
                    size: file.size,
                    variant,
                    storagePath,
                    downloadURL,
                    uploadedAt: now
                  }
                ],
                metadata: {
                  originalUploader: this.currentUser.uid,
                  uploadMethod: 'admin_interface',
                  uploadTimestamp: new Date().toISOString(),
                  fileHash: await this.calculateFileHash(file)
                }
              };

              await setDoc(documentRef, documentData);

              // Log audit trail (with error handling - don't fail upload if logging fails)
              try {
                await auditTrailService.logAction('document.admin_upload', {
                  targetUserId: learnerId,
                  targetUserEmail: learnerData?.email,
                  documentId: documentRef.id,
                  documentCategory: categoryInfo.id,
                  fileName: customFileName,
                  fileSize: file.size,
                  uploadReason: additionalInfo.reason || 'Administrative upload',
                  notes: additionalInfo.notes || '',
                  adminUploadConfirmed: true
                });
                devLog('✅ Admin upload audit logged successfully');
              } catch (auditError) {
                devLog('⚠️ Failed to log admin upload audit trail:', auditError);
                // Don't fail the upload if audit logging fails
              }

              // Dispatch event to notify other components that a document was uploaded
              window.dispatchEvent(new CustomEvent('documentUploaded', {
                detail: {
                  learnerId: learnerId,
                  documentId: documentRef.id,
                  category: categoryInfo.id,
                  adminUpload: true
                }
              }));

              resolve({
                success: true,
                documentId: documentRef.id,
                downloadURL,
                message: `Document uploaded successfully for ${learnerData?.displayName || 'learner'}`
              });
            } catch (error) {
              devError('Error saving document record:', error);
              reject(new Error('Failed to save document record: ' + error.message));
            }
          }
        );
      });
    } catch (error) {
      devError('Error uploading document:', error);
      throw new Error('Failed to upload document: ' + error.message);
    }
  }

  /**
   * Bulk upload documents for multiple learners
   */
  async bulkUploadDocuments(uploads) {
    await this.initialize();

    if (!this.hasUploadPermission()) {
      throw new Error('Access denied: Insufficient permissions');
    }

    const results = {
      successful: [],
      failed: [],
      total: uploads.length
    };

    for (const upload of uploads) {
      try {
        const result = await this.uploadDocumentForLearner(
          upload.learnerId,
          upload.file,
          upload.category,
          upload.additionalInfo || {}
        );

        results.successful.push({
          learnerId: upload.learnerId,
          category: upload.category,
          documentId: result.documentId,
          message: result.message
        });

      } catch (error) {
        results.failed.push({
          learnerId: upload.learnerId,
          category: upload.category,
          error: error.message
        });
      }
    }

    // Log bulk upload action (with error handling)
    try {
      await auditTrailService.logAction('document.bulk_admin_upload', {
        totalUploads: results.total,
        successfulUploads: results.successful.length,
        failedUploads: results.failed.length,
        uploadedBy: this.currentUser.uid
      });
      devLog('✅ Bulk upload audit logged successfully');
    } catch (auditError) {
      devLog('⚠️ Failed to log bulk upload audit trail:', auditError);
      // Don't fail the operation if audit logging fails
    }

    return results;
  }

  /**
   * Calculate file hash for integrity verification
   */
  async calculateFileHash(file) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      devLog('Could not calculate file hash:', error);
      return 'hash_unavailable';
    }
  }

  /**
   * Get upload history for audit purposes
   */
  async getUploadHistory(filters = {}) {
    if (!this.hasUploadPermission()) {
      throw new Error('Access denied: Insufficient permissions');
    }

    try {
      return await auditTrailService.getAuditTrail({
        action: 'document.admin_upload',
        userId: filters.uploadedBy || null,
        startDate: filters.startDate || null,
        endDate: filters.endDate || null,
        limit: filters.limit || 100
      });
    } catch (error) {
      devError('Error getting upload history:', error);
      throw new Error('Failed to get upload history: ' + error.message);
    }
  }

  /**
   * Delete document uploaded by admin (with audit trail)
   */
  async deleteAdminUploadedDocument(learnerId, documentId, reason) {
    await this.initialize();

    if (!this.hasUploadPermission()) {
      throw new Error('Access denied: Insufficient permissions');
    }

    const canAccess = await this.canAccessLearner(learnerId);
    if (!canAccess) {
      throw new Error('Access denied: Cannot access this learner');
    }

    try {
      // Get document details before deletion
      const docRef = doc(db, 'users', learnerId, 'documents', documentId);
      const docSnapshot = await getDoc(docRef);

      if (!docSnapshot.exists()) {
        throw new Error('Document not found');
      }

      const documentData = docSnapshot.data();

      // Mark as deleted (soft delete)
      await updateDoc(docRef, {
        status: 'deleted',
        deletedAt: serverTimestamp(),
        deletedBy: this.currentUser.uid,
        deletionReason: reason,
        deletedByRole: this.userRole
      });

      // Log deletion (with error handling)
      try {
        await auditTrailService.logAction('document.admin_delete', {
          targetUserId: learnerId,
          documentId: documentId,
          documentCategory: documentData.category,
          fileName: documentData.fileName,
          deletionReason: reason,
          originalUploader: documentData.uploadedBy
        });
        devLog('✅ Document deletion audit logged successfully');
      } catch (auditError) {
        devLog('⚠️ Failed to log document deletion audit trail:', auditError);
        // Don't fail the deletion if audit logging fails
      }

      return {
        success: true,
        message: 'Document deleted successfully'
      };

    } catch (error) {
      devError('Error deleting document:', error);
      throw new Error('Failed to delete document: ' + error.message);
    }
  }
}

// Create and export singleton instance
const adminDocumentUploadService = new AdminDocumentUploadService();
export default adminDocumentUploadService;
